import { useCallback, useMemo, useRef, useState } from 'react';
import { roleForId, ROLE_COLOR_SWATCHES, type DrillRolePlayer } from './playerRoles';
import { pitchViewBox, pitchBackgroundRect, drillNormToPitchSvg, pitchSvgToDrillNorm, type PitchView, type CustomPitch } from './pitchArea';
import type { PitchSpec } from './pitchSpecs';

interface PlayerRoleOverlay2DProps {
  players: DrillRolePlayer[];
  onChange: (next: DrillRolePlayer[]) => void;
  /** Armed role for click-to-place; null = idle (overlay is click-through). */
  activeRoleId: string | null;
  spec: PitchSpec;
  view: PitchView;
  custom?: CustomPitch | null;
}

/** Role-id → single-letter prefix used for the auto-label (A1, D1 …). */
const ROLE_PREFIX: Record<string, string> = {
  attacker: 'A',
  defender: 'D',
  neutral: 'N',
  bumper: 'B',
  server: 'S',
  coach: 'C',
  goalkeeper: 'GK',
};

/** Next label for the supplied role given the current player list.
 *  Counts existing players with the same roleId and returns the next
 *  sequential label. Ball is unlabeled (its glyph is the ball graphic). */
function nextLabelFor(roleId: string, players: DrillRolePlayer[]): string | undefined {
  if (roleId === 'ball') return undefined;
  const prefix = ROLE_PREFIX[roleId] ?? roleId.slice(0, 1).toUpperCase();
  const count = players.filter((p) => p.roleId === roleId).length;
  return `${prefix}${count + 1}`;
}

export function PlayerRoleOverlay2D({
  players,
  onChange,
  activeRoleId,
  spec,
  view,
  custom,
}: PlayerRoleOverlay2DProps): React.JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const eventToNorm = useCallback((evt: React.PointerEvent<SVGSVGElement>): [number, number] | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const local = pt.matrixTransform(ctm.inverse());
    return pitchSvgToDrillNorm(local.x, local.y, spec, view, custom);
  }, [spec, view, custom]);

  const handleBackgroundDown = useCallback((evt: React.PointerEvent<SVGSVGElement>) => {
    if (!activeRoleId) return;
    if ((evt.target as Element).getAttribute('data-role-bg') !== '1') return;
    const norm = eventToNorm(evt);
    if (!norm) return;
    const next: DrillRolePlayer = {
      id: `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`,
      roleId: activeRoleId,
      position: norm,
      label: nextLabelFor(activeRoleId, players),
    };
    onChange([...players, next]);
    setSelectedId(next.id);
  }, [activeRoleId, eventToNorm, onChange, players]);

  const handleTokenDown = useCallback((id: string) => (evt: React.PointerEvent<SVGGElement>) => {
    evt.stopPropagation();
    setSelectedId(id);
    setDragId(id);
    (evt.currentTarget as Element).setPointerCapture?.(evt.pointerId);
  }, []);

  const handlePointerMove = useCallback((evt: React.PointerEvent<SVGSVGElement>) => {
    if (!dragId) return;
    const norm = eventToNorm(evt);
    if (!norm) return;
    onChange(players.map((p) => (p.id === dragId ? { ...p, position: norm } : p)));
  }, [dragId, players, eventToNorm, onChange]);

  const handlePointerUp = useCallback(() => {
    setDragId(null);
  }, []);

  // The SVG always listens so a placed token can be dragged in Select
  // mode. The background <rect> only activates when a role tool is
  // armed. Use 'all' (not 'auto') because the rect has
  // fill="transparent" and SVG's visiblePainted treats that as
  // not-painted for hit-testing.
  const bgPointerEvents = activeRoleId !== null ? 'all' : 'none';

  // Token radius scales with the visible region's width so a 15×15 m
  // grid renders the same player-to-pitch proportion as a full pitch.
  const regionWidth = view === 'custom' && custom
    ? (custom.unit === 'ft' ? custom.width * 0.3048 : custom.width)
    : view === 'quarter'
    ? spec.width / 2
    : spec.width;
  // 20% smaller than the original (0.045 × 0.8 = 0.036). Min cap
  // drops proportionally so tiny pitches stay readable.
  const tokenR = Math.max(0.64, regionWidth * 0.036);

  // Sort: balls go LAST so they paint on top of stacked role tokens.
  const orderedPlayers = useMemo(
    () => [...players].sort((a, b) => Number(a.roleId === 'ball') - Number(b.roleId === 'ball')),
    [players],
  );

  return (
    <svg
      ref={svgRef}
      viewBox={pitchViewBox(spec, view, custom)}
      preserveAspectRatio="xMidYMid meet"
      onPointerDown={handleBackgroundDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        // SVG itself goes click-through when nothing's armed AND no
        // drag is in progress. Tokens override with their own
        // pointer-events: 'auto' so they remain draggable.
        pointerEvents: activeRoleId !== null || dragId !== null ? 'auto' : 'none',
      }}
      aria-label="Drill players overlay"
    >
      <rect
        data-role-bg="1"
        {...pitchBackgroundRect(spec, view, custom)}
        fill="transparent"
        style={{
          cursor: activeRoleId ? 'crosshair' : 'default',
          pointerEvents: bgPointerEvents,
        }}
      />
      {orderedPlayers.map((p) => {
        const role = roleForId(p.roleId);
        const { cx, cy } = drillNormToPitchSvg(p.position, spec, view, custom);
        const isSelected = selectedId === p.id;
        const isBall = role.id === 'ball';
        // Ball is small + distinct — coach reads it as the ball, not a
        // labelled player. Token radius is for the role tokens.
        const r = isBall ? tokenR * 0.32 : tokenR;
        // Per-token override > role default. Lets the coach mark a key
        // player or pick the bib colour they're actually using.
        const fillColor = p.color ?? role.color;
        return (
          <g
            key={p.id}
            onPointerDown={handleTokenDown(p.id)}
            style={{ cursor: 'grab', pointerEvents: 'auto' }}
            data-player-id={p.id}
            data-player-role={p.roleId}
            data-player-label={p.label ?? ''}
          >
            {!isBall && (
              <ellipse cx={cx} cy={cy + r * 0.9} rx={r} ry={r * 0.35} fill="rgba(0,0,0,0.45)" />
            )}
            {isSelected && (
              <circle cx={cx} cy={cy} r={r * 1.55} fill="none" stroke="#27AE60" strokeWidth={r * 0.25} opacity={0.9} />
            )}
            {isBall ? (
              <BallToken cx={cx} cy={cy} r={r} />
            ) : (
              <>
                <circle
                  cx={cx} cy={cy}
                  r={r}
                  fill={fillColor}
                  stroke="rgba(0,0,0,0.55)"
                  strokeWidth={r * 0.13}
                />
                <text
                  x={cx} y={cy + r * 0.4}
                  textAnchor="middle"
                  fontSize={r * 1.05}
                  fontWeight={700}
                  fill="#fff"
                  pointerEvents="none"
                >
                  {p.label ?? role.glyph ?? ''}
                </text>
              </>
            )}
          </g>
        );
      })}

      {/* Colour swatch row for the selected (non-ball) token. Coach
          taps a swatch to override the role's default colour for this
          one player. */}
      {selectedId && (() => {
        const selected = players.find((p) => p.id === selectedId);
        if (!selected || selected.roleId === 'ball') return null;
        const { cx, cy } = drillNormToPitchSvg(selected.position, spec, view, custom);
        const swatchR = tokenR * 0.30;
        const gap = swatchR * 2.6;
        const totalW = gap * (ROLE_COLOR_SWATCHES.length - 1);
        const startX = cx - totalW / 2;
        const y = cy - tokenR * 1.9;
        return (
          <g data-testid="player-color-picker" pointerEvents="auto">
            <rect
              x={startX - swatchR * 1.4}
              y={y - swatchR * 1.5}
              width={totalW + swatchR * 2.8}
              height={swatchR * 3}
              rx={swatchR * 1.2}
              ry={swatchR * 1.2}
              fill="rgba(15,25,45,0.85)"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth={swatchR * 0.08}
            />
            {ROLE_COLOR_SWATCHES.map((c, idx) => {
              const x = startX + idx * gap;
              const isCurrent = (selected.color ?? roleForId(selected.roleId).color).toLowerCase() === c.toLowerCase();
              return (
                <g
                  key={c}
                  data-color={c}
                  style={{ cursor: 'pointer' }}
                  onPointerDown={(evt) => {
                    evt.stopPropagation();
                    onChange(players.map((q) => (q.id === selectedId ? { ...q, color: c } : q)));
                  }}
                >
                  <circle
                    cx={x} cy={y} r={swatchR}
                    fill={c}
                    stroke={isCurrent ? '#27AE60' : 'rgba(0,0,0,0.55)'}
                    strokeWidth={isCurrent ? swatchR * 0.25 : swatchR * 0.12}
                  />
                </g>
              );
            })}
          </g>
        );
      })()}
    </svg>
  );
}

/** Ball token — small white circle, no surrounding stroke. */
function BallToken({ cx, cy, r }: { cx: number; cy: number; r: number }): React.JSX.Element {
  return <circle cx={cx} cy={cy} r={r} fill="#fff" />;
}
